import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Users, HardHat, ImageIcon, BookOpen, Wrench, LifeBuoy, Home, ChevronDown, Layers, FolderOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Custom Scaffolding icon — vertical stacked rectangles with X bracing
const ScaffoldIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="5" y="3" width="14" height="18" rx="0.5" />
    <line x1="5" y1="12" x2="19" y2="12" />
    <line x1="5" y1="3" x2="19" y2="12" />
    <line x1="19" y1="3" x2="5" y2="12" />
    <line x1="5" y1="12" x2="19" y2="21" />
    <line x1="19" y1="12" x2="5" y2="21" />
  </svg>
);

// Reusable top-level dropdown menu. Opens on click, closes on outside click,
// on route change or Esc. Keyboard accessible.
function DropdownMenu({ label, icon: Icon, testId, items, activeMatchPrefixes = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isActive = activeMatchPrefixes.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-full transition-colors ${
          isActive || open ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
        }`}
        data-testid={testId}
      >
        <Icon className="w-4 h-4" />
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 sm:left-0 top-full mt-2 min-w-[200px] bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden z-50"
          data-testid={`${testId}-menu`}
        >
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              role="menuitem"
              end={it.end}
              className={({ isActive: a }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                  a ? "bg-[#ff5a00]/10 text-[#ff5a00]" : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
              data-testid={it.testId}
            >
              {it.icon ? <it.icon className="w-4 h-4" /> : null}
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navCls = ({ isActive }) =>
    `inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold whitespace-nowrap rounded-full transition-colors ${
      isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
    }`;

  const assetItems = [
    { to: "/scaffolds", label: "Scaffalature", icon: ScaffoldIcon, testId: "nav-asset-scaffolds" },
    { to: "/dpi", label: "DPI", icon: HardHat, testId: "nav-asset-dpi" },
    { to: "/strumenti", label: "Strumenti", icon: Wrench, testId: "nav-asset-strumenti" },
    { to: "/anticaduta", label: "Anticaduta", icon: LifeBuoy, testId: "nav-asset-anticaduta" },
  ];

  const risorseItems = [
    { to: "/gallery", label: "Galleria", icon: ImageIcon, testId: "nav-risorse-gallery" },
    ...(isAdmin
      ? [
          { to: "/manuals", label: "Manuali", icon: BookOpen, testId: "nav-risorse-manuals" },
          { to: "/clients", label: "Clienti", icon: Users, testId: "nav-risorse-clients" },
        ]
      : []),
  ];

  return (
    <header
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-neutral-200/70"
      data-testid="navbar"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
          <img src="/logo-union.png" alt="Union" className="h-10 w-auto object-contain rounded-xl" />
          <div className="leading-tight hidden sm:block">
            <div className="font-bold text-base tracking-tight">
              UNION<span className="text-[#ff5a00]"> </span>CHECK
            </div>
            <div className="tag-label text-[#ff5a00]">SICURI SEMPER</div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 overflow-visible shrink min-w-0">
          <NavLink to="/" end className={navCls} data-testid="nav-home-link">
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Home</span>
          </NavLink>

          <DropdownMenu
            label="Asset"
            icon={Layers}
            testId="nav-asset-trigger"
            items={assetItems}
            activeMatchPrefixes={["/scaffolds", "/dpi", "/strumenti", "/anticaduta"]}
          />

          <DropdownMenu
            label="Risorse"
            icon={FolderOpen}
            testId="nav-risorse-trigger"
            items={risorseItems}
            activeMatchPrefixes={["/gallery", "/manuals", "/clients"]}
          />

          {user && user !== false && (
            <>
              <div className="hidden lg:flex flex-col items-end leading-tight ml-3 pl-3 border-l border-neutral-200">
                <div className="tag-label">{user.role === "admin" ? "OPERATORE" : "CLIENTE"}</div>
                <div className="text-sm font-semibold truncate max-w-[160px]" data-testid="nav-user-name">{user.name}</div>
              </div>
              <button
                onClick={onLogout}
                className="bru-btn bru-btn-secondary !px-3 !py-2 ml-2"
                data-testid="nav-logout-button"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Esci</span>
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
